/**
 * Under an answer, the files it names — what the AI made — with "Abrir" and "Mostrar na pasta" (docs/refactor/51,
 * N6), so somebody who does not program never has to find a path by hand. Desktop only, and only for files the app
 * may touch (src-tauri/src/reveal.rs says which); in a browser the files are on the person's computer, not here.
 */

import { useQuery } from '@tanstack/react-query'
import { ExternalLink, FileText, FolderOpen } from 'lucide-react'
import { useState } from 'react'
import { isDesktop, pathOpen, pathReveal, pathsOpenable } from '../lib/desktop'
import { fileName, filesMentioned } from '../lib/fileMentions'
import { useI18n } from '../lib/i18n'

export function FileShortcuts({ text, folder }: { text: string; folder: string | null }) {
  const { t } = useI18n()
  const desktop = isDesktop()
  const mentioned = desktop ? filesMentioned(text, folder) : []
  const [problem, setProblem] = useState<string | null>(null)
  const { data: openable } = useQuery({
    queryKey: ['paths-openable', mentioned],
    queryFn: () => pathsOpenable(mentioned),
    enabled: mentioned.length > 0,
    staleTime: 30_000,
  })
  const files = mentioned.filter((_, index) => openable?.[index])
  if (files.length === 0) return null

  const act = (work: () => Promise<void>) => () => {
    setProblem(null)
    void work().catch((error) => setProblem(String(error).includes('runs') ? t('files.runs') : t('files.cannot')))
  }

  return (
    <div className="file-shortcuts" aria-label={t('files.title')}>
      {files.map((path) => (
        <div key={path} className="file-shortcuts__row" title={path}>
          <FileText size={14} aria-hidden />
          <span className="small truncate file-shortcuts__name">{fileName(path)}</span>
          <button type="button" className="btn btn--sm" onClick={act(() => pathOpen(path))}>
            <ExternalLink size={12} /> {t('files.open')}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={act(() => pathReveal(path))}>
            <FolderOpen size={12} /> {t('files.reveal')}
          </button>
        </div>
      ))}
      {problem && <p className="tiny" role="alert" style={{ margin: 0 }}>{problem}</p>}
    </div>
  )
}
